/**
 * ComparisonTable — a feature/tier comparison grid. First column is the row
 * label; booleans render as check/cross, strings render verbatim. Wide tables
 * can be opened full-size in a lightbox (Dialog). Composes the Table primitive +
 * Dialog + lucide icons. Tokens + Tailwind only.
 */
import type { ReactNode } from 'react';
import { Check, Minus, Expand } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../table';
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '../dialog';
import { CollapsibleTable } from './CollapsibleTable';

export type ComparisonCell = string | number | boolean;

/** Tables with at least this many rows get a collapse toggle so they don't
 *  force scrolling; shorter tables render plain. */
const COLLAPSE_AT = 8;

export interface ComparisonTableProps {
  /** Column headers; index 0 is the row-label column header (often ""). */
  readonly columns: readonly string[];
  /** Each row: cell[0] is the row label, the rest align with columns[1..]. */
  readonly rows: readonly (readonly ComparisonCell[])[];
  readonly caption?: ReactNode;
  /** Click to open the full table in a lightbox (default true). */
  readonly expandable?: boolean;
  /** Collapse-header label for long tables (default "Comparison"). */
  readonly title?: string;
  /** Start fully expanded (default false → peek the first rows). */
  readonly defaultOpen?: boolean;
}

function renderCell(value: ComparisonCell): ReactNode {
  if (value === true) return <Check className="mx-auto size-4 text-success" aria-label="Yes" />;
  if (value === false) return <Minus className="mx-auto size-4 text-muted-foreground" aria-label="No" />;
  return value;
}

function TableInner({ columns, rows }: Pick<ComparisonTableProps, 'columns' | 'rows'>) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((col, i) => (
            <TableHead key={i} className={`text-xl text-foreground ${i === 0 ? 'min-w-40' : 'text-center'}`}>{col}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, r) => (
          <TableRow key={r} className="odd:bg-muted/30">
            {row.map((cell, c) => (
              <TableCell key={c} className={c === 0 ? 'font-medium text-foreground' : 'text-center'}>
                {renderCell(cell)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ComparisonBody({ columns, rows, caption, expandable }: Pick<ComparisonTableProps, 'columns' | 'rows' | 'caption' | 'expandable'>) {
  return (
    <div className="group relative overflow-x-auto">
      {expandable && (
        <Dialog>
          <DialogTrigger asChild>
            <button
              type="button"
              aria-label="Expand table"
              className="absolute right-2 top-2 z-10 inline-flex size-8 items-center justify-center rounded-md bg-background/70 text-muted-foreground opacity-0 backdrop-blur transition-opacity hover:text-foreground group-hover:opacity-100"
            >
              <Expand className="size-4" aria-hidden="true" />
            </button>
          </DialogTrigger>
          <DialogContent size="full" className="bg-card p-4">
            <DialogTitle className="sr-only">{String(columns.filter(Boolean)[0] ?? 'Table')}</DialogTitle>
            <div className="overflow-x-auto">
              <TableInner columns={columns} rows={rows} />
            </div>
            {caption && <p className="mt-3 text-sm text-muted-foreground">{caption}</p>}
          </DialogContent>
        </Dialog>
      )}
      <TableInner columns={columns} rows={rows} />
      {caption && <p className="px-4 py-3 text-sm text-muted-foreground">{caption}</p>}
    </div>
  );
}

export function ComparisonTable({ columns, rows, caption, expandable = true, title, defaultOpen = false }: ComparisonTableProps) {
  const body = <ComparisonBody columns={columns} rows={rows} caption={caption} expandable={expandable} />;

  // Long tables get a collapse toggle so they don't force scrolling.
  if (rows.length >= COLLAPSE_AT) {
    return (
      <CollapsibleTable title={title ?? 'Comparison'} summary={`${rows.length} rows`} defaultOpen={defaultOpen}>
        {body}
      </CollapsibleTable>
    );
  }
  return <div className="overflow-hidden rounded-card border border-border">{body}</div>;
}
